import { cn } from '@/lib/utils';
import * as React from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage, useFormField } from '../ui/form';
import { Checkbox } from '../ui/checkbox';

export interface FormCheckboxProps extends Omit<React.ComponentPropsWithoutRef<typeof Checkbox>, 'form' | 'label' | 'defaultValue'> {
    form: UseFormReturn<any>;
    label?: string;
    name: string;
    placeholder?: string;
    render?: (field: FieldValues) => React.ReactNode;
    defaultValue?: boolean;
    isRequired?: boolean;
    description?: string;
}

const FormCheckbox = React.forwardRef<React.ElementRef<typeof Checkbox>, FormCheckboxProps>(
    ({ className, name, form, label, placeholder, render, defaultValue = false, isRequired = false, description, ...props }, ref) => {
        const { formItemId, formDescriptionId, formMessageId } = useFormField();

        return (
            <FormField
                control={form.control}
                name={name}
                defaultValue={defaultValue}
                render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                    <FormItem className='flex flex-row items-start space-x-1 space-y-0 items-center'>
                        <FormControl>
                            {render ? (
                                render({ onChange, value, ...field })
                            ) : (
                                <Checkbox
                                    ref={ref}
                                    checked={value}
                                    onCheckedChange={onChange}
                                    className={cn('mt-0', className, error && 'border-destructive focus-visible:ring-destructive')}
                                    id={formItemId}
                                    aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
                                    aria-invalid={!!error}
                                    {...props}
                                />
                            )}
                        </FormControl>
                        <div className="space-y-1 leading-none">
                            <FormLabel htmlFor={formItemId} className="text-sm font-normal cursor-pointer">
                                {label}
                                {' '}
                                {isRequired && <span className="text-red-500">*</span>}
                            </FormLabel>
                            {description && (
                                <p className="text-sm text-muted-foreground" id={formDescriptionId}>
                                    {description}
                                </p>
                            )}
                            <FormMessage />
                        </div>
                    </FormItem>
                )}
            />
        );
    },
);
FormCheckbox.displayName = 'FormCheckbox';

export { FormCheckbox };
